import * as R from 'ramda';

import * as api from 'service/ErplyAPI/user';
import { EMPLOYEE_ID } from 'utils/constants';
import { addError, addSuccess } from 'rdx/alert';
import { getAccountAdminUserRights } from 'service/AccountAdmin';

import * as types from './types';

export const fetchUserGroups = (params) => async (dispatch) => {
  dispatch({ type: types.FETCH_USER_GROUPS.START });
  try {
    const { records } = await api.getUserGroups(params);
    dispatch({ type: types.FETCH_USER_GROUPS.SUCCESS, payload: records });
  } catch (err) {
    dispatch(addError(err.message));
    dispatch({ type: types.FETCH_USER_GROUPS.ERROR, payload: err.message });
  }
};

export const fetchEmployees = () => async (dispatch) => {
  dispatch({ type: types.FETCH_EMPLOYEES.START });
  try {
    const { records } = await api.getEmployees({ recordsOnPage: 100 });
    dispatch({ type: types.FETCH_EMPLOYEES.SUCCESS, payload: records });
  } catch (err) {
    dispatch(addError(err.message));
    dispatch({ type: types.FETCH_EMPLOYEES.ERROR, payload: err.message });
  }
};

export const fetchCurrentEmployee = () => async (dispatch) => {
  dispatch({ type: types.FETCH_CURRENT_EMPLOYEE.START });
  const employeeID = localStorage.getItem(EMPLOYEE_ID);
  try {
    const data = await api.getEmployees({ employeeID });
    const employee = data.records[0];
    dispatch({ type: types.FETCH_CURRENT_EMPLOYEE.SUCCESS, payload: employee });
  } catch (err) {
    dispatch(addError(err.message));
    dispatch({
      type: types.FETCH_CURRENT_EMPLOYEE.ERROR,
      payload: err.message,
    });
  }
};

const moduleWithFallback = (mod, fallbackModule) => {
  const { edit, add, view, delete: del, ...rest } = mod;
  return {
    edit: edit ?? fallbackModule.edit,
    add: add ?? fallbackModule.add,
    view: view ?? fallbackModule.view,
    delete: del ?? fallbackModule.delete,
    ...rest,
  };
};

export const fetchCurrentUserRights = () => async (dispatch) => {
  dispatch({ type: types.SET_USER_RIGHTS.START });

  try {
    // Erply user rights
    const {
      records: [userRights],
    } = await api.getErplyUserRights({
      getCurrentUser: 1,
    });

    // Account Admin user rights
    const {
      data: { data },
    } = await getAccountAdminUserRights();

    // Convert AA user rights into a dictionary to enable deepMergeRight with Erply rights later
    const objectifiedAccoutAdminModules = data.reduce(
      (obj, module) => ({
        ...obj,
        // temporary workaround; fixes issue where AccountAdmin API and Erply API return different user permissions for POS user
        [module.sectionName]: moduleWithFallback(
          module,
          userRights.modules[module.sectionName],
        ),
      }),
      {},
    );

    // Combine both rights
    const finalUserModules = Object.entries(
      // Merge the ErplyAPI modules dict with the AA modules dict with taking AA modules as priority
      R.mergeDeepRight(userRights.modules, objectifiedAccoutAdminModules),
      // Then convert dictionary into array of objects witha `sectionName` prop via map
    ).map(([key, value]) => ({ ...value, sectionName: key }));

    dispatch({
      type: types.SET_USER_RIGHTS.SUCCESS,
      payload: { ...userRights, modules: finalUserModules },
    });
  } catch (error) {
    dispatch(addError(error.message ?? 'Failed to get user rights'));
    dispatch({ type: types.SET_USER_RIGHTS.ERROR, payload: error.message });
  }
};

export const saveEmployee = (employeeData) => async (dispatch) => {
  dispatch({ type: types.SAVE_EMPLOYEE.START });
  try {
    const { records } = await api.saveEmployee(employeeData);
    const { employeeID } = records[0];
    dispatch({
      type: types.SAVE_EMPLOYEE.SUCCESS,
      payload: { ...employeeData, employeeID },
    });
    return employeeID;
  } catch (err) {
    dispatch(addError(err.message));
    dispatch({ type: types.SAVE_EMPLOYEE.ERROR, payload: err.message });
    return null;
  }
};

export const saveUser =
  (userInfo, callback = null) =>
  async (dispatch) => {
    let employeeID;
    dispatch({ type: types.SAVE_USER.START });
    try {
      employeeID = await dispatch(
        saveEmployee({
          firstName: userInfo.firstName,
          lastName: userInfo.lastName,
          email: userInfo.name,
          employeeID: userInfo.employeeID,
        }),
      );
      if (!employeeID) {
        dispatch({
          type: types.SAVE_USER.ERROR,
          payload: 'SaveEmployee did not return employeeID',
        });
        return null;
      }
      const { records } = await api.saveUser({ ...userInfo, employeeID });
      const { userID } = records[0];
      dispatch({
        type: types.SAVE_USER.SUCCESS,
        payload: { employeeID, userID },
      });
      callback?.();
      dispatch(addSuccess('User added successfully'));
      return employeeID;
    } catch (err) {
      dispatch(addError(err.message));
      dispatch({ type: types.SAVE_USER.ERROR, payload: err.message });
      return employeeID;
    }
  };

export const deleteEmployee = (employeeID) => async (dispatch) => {
  dispatch({ type: types.DELETE_EMPLOYEE.START });
  try {
    await api.deleteEmployee({ employeeID });
    dispatch({ type: types.DELETE_EMPLOYEE.SUCCESS, payload: employeeID });
  } catch (err) {
    dispatch(addError(err.message));
    dispatch({ type: types.DELETE_EMPLOYEE.ERROR, payload: err.message });
  }
};
